import { ReactNode, useEffect, useRef, useState } from "react";

import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { useMyDeliveryQuote } from "@/Apollo/hooks/product/useMyDeliveryQuote";
import { useRequestDeliveryQuote } from "@/Apollo/hooks/product/useRequestDeliveryQuote";
import {
  DeliveryQuoteDeliveryType,
  DeliveryQuoteFragmentFragment,
} from "@/Apollo/schema";
import { useAuth } from "@/auth";
import { ProductBySlugNonNullable } from "@/common/contracts/pdp.contracts";
import { useGuestModalGuard } from "@/common/hooks/guest.hooks";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import { getRateLimitCopy } from "@/common/utils/graphql-error.utils";
import { formatDateTimeWithZone } from "@/common/utils/intl.utils";
import { ProductDeliveryOptionItemListSkeleton } from "@/features/product-delivery-options/components/ProductDeliveryOptionItemListSkeleton";
import { UsDeliveryPrice } from "@/features/us-delivery/components/UsDeliveryPrice";
import { UsDeliveryQuoteForm } from "@/features/us-delivery/components/UsDeliveryQuoteForm";
import {
  UsQuoteFormValues,
  UsQuoteRequestError,
  canRequestAgain,
  getUsQuoteRequestError,
  getUsQuoteView,
  toFormValues,
} from "@/features/us-delivery/us-delivery.utils";
import { showModal } from "@/ui/Modal2";
import { Button } from "@/ui/cva/Button";
import { Flavor } from "@/ui/typography/Flavor";

type UsDeliveryQuoteProps = {
  product: Pick<ProductBySlugNonNullable, "id" | "title">;
};

/**
 * WHO-6509. "Request a quote" for a courier listing on `/en-us` (WHO-6499, Evelien's quote flow):
 * A before a request, B the form, C requested, D quoted (fee and valid until), then expired, item
 * sold or paid, from which the buyer can ask again.
 *
 * The quote is the viewer's own, so it is read after mount and never in the server render: the
 * product page is cached publicly. Nothing shows until sign-in has settled, so a signed-in buyer
 * never sees state A (or the login wall) for a moment. Paying the fee is checkout's part of
 * WHO-6499; until then the page's own buy button stays as it is.
 */
export const UsDeliveryQuote = ({ product }: UsDeliveryQuoteProps) => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const { isLoggedIn, isReady } = useAuth();
  const { guestGuard } = useGuestModalGuard();

  const [formOpen, setFormOpen] = useState(false);
  const [requestError, setRequestError] = useState<string>();
  /* Set after the login wall: the form waits for the buyer's quote to load (see the effect). */
  const [openAfterLogin, setOpenAfterLogin] = useState(false);
  const blockRef = useRef<HTMLDivElement>(null);
  const restoreFocus = useRef(false);

  const { data, loading, error } = useMyDeliveryQuote({
    variables: { id: product.id },
    skip: !isReady || !isLoggedIn,
  });
  const [requestQuote, { loading: submitting }] = useRequestDeliveryQuote(
    product.id
  );

  /* Read only for a signed-in buyer: a guest's page never shows anyone's quote. */
  const quote = isLoggedIn ? data?.product?.myDeliveryQuote ?? null : null;
  const view = getUsQuoteView(quote, new Date());

  const requestErrorCopy: Record<UsQuoteRequestError, string> = {
    invalidAddress: t("pdp:pdp-us-delivery-error-address", {
      defaultValue: "Check the ZIP code, state and city.",
    }),
    limitReached: t("pdp:pdp-us-delivery-error-limit-open", {
      defaultValue:
        "You have the most open delivery quotes allowed. Buy one of the items or wait for a quote to close before asking for another.",
    }),
    notQuotable: t("pdp:pdp-us-delivery-error-not-quotable", {
      defaultValue: "We can't quote delivery to the US for this item.",
    }),
    sessionExpired: t("pdp:pdp-us-delivery-error-session", {
      defaultValue: "Your session has ended. Log in again to request a quote.",
    }),
    unavailable: t("pdp:pdp-us-delivery-error-unavailable", {
      defaultValue: "Delivery quotes to the US aren't available right now.",
    }),
    generic: t("pdp:pdp-us-delivery-error-generic", {
      defaultValue: "Something went wrong. Please try again.",
    }),
  };

  const deliveryTypeCopy: Record<DeliveryQuoteDeliveryType, string> = {
    [DeliveryQuoteDeliveryType.Door]: t("pdp:pdp-us-delivery-type-door", {
      defaultValue: "To the door",
    }),
    [DeliveryQuoteDeliveryType.Room]: t("pdp:pdp-us-delivery-type-room", {
      defaultValue: "Into the room",
    }),
  };

  /* The deadline is an exact moment, so it carries the time and the buyer's time zone. */
  const formatDeadline = (value: string) =>
    formatDateTimeWithZone(value, locale);

  const loginWallDescription = t("pdp:pdp-us-delivery-login-wall", {
    productTitle: product.title,
    defaultValue: "Log in to request a delivery quote for {{productTitle}}.",
    interpolation: { escapeValue: false },
  });

  const showForm = () => {
    setRequestError(undefined);
    setFormOpen(true);
  };

  const closeForm = () => {
    restoreFocus.current = true;
    setFormOpen(false);
  };

  const openForm = async () => {
    if (isLoggedIn) {
      showForm();
      return;
    }

    const pass = await guestGuard({
      description: loginWallDescription,
      referrer: "us_delivery_quote",
    });

    if (pass) {
      setOpenAfterLogin(true);
    }
  };

  /*
   * After the login wall the buyer may turn out to have a quote already. The gateway answers a
   * repeat request with that open quote and ignores the new address, so the form only opens once
   * their quote has loaded and allows another request; otherwise the page shows that quote.
   */
  useEffect(() => {
    if (!openAfterLogin || !isLoggedIn || loading || (!data && !error)) {
      return;
    }

    setOpenAfterLogin(false);

    if (!error && (view === "none" || canRequestAgain(view))) {
      showForm();
    }
  }, [openAfterLogin, isLoggedIn, loading, data, error, view]);

  /* Back to the block when the form closes, rather than to the top of the page. */
  useEffect(() => {
    if (!formOpen && restoreFocus.current) {
      restoreFocus.current = false;
      blockRef.current?.focus();
    }
  }, [formOpen]);

  /* Nothing to show yet: the buyer's quote is loading for the first time. */
  const quoteLoading = loading && !data;

  const handleSubmit = async (values: UsQuoteFormValues) => {
    /*
     * The quote is reloading (after logging back in, Apollo still hands over the answer from
     * before the session ended): wait for it, so the check below reads the buyer's real quote.
     */
    if (loading) {
      return;
    }

    setRequestError(undefined);

    /*
     * After logging back in (an ended session), the reloaded quote may turn out to be open. The
     * gateway would answer this request with that quote and drop the address typed here, so the
     * page shows the open quote instead of sending the form.
     */
    if (view !== "none" && !canRequestAgain(view)) {
      closeForm();
      return;
    }

    try {
      await requestQuote({
        productId: product.id,
        postalCode: values.postalCode,
        region: values.region,
        city: values.city,
        deliveryType: values.deliveryType,
        note: values.note || null,
      });
      closeForm();
    } catch (requestFailure) {
      const kind = getUsQuoteRequestError(requestFailure);

      setRequestError(
        getRateLimitCopy(requestFailure, t) ?? requestErrorCopy[kind]
      );

      /* The form stays filled in, so the buyer can send it again once logged back in. */
      if (kind === "sessionExpired") {
        /* As `guestGuard` does, so the sign-up funnel counts this login wall too. */
        analyticsService.trackSignUpBlock("us_delivery_quote");
        void showModal("signup-login-wall", {
          description: loginWallDescription,
        });
      }
    }
  };

  /*
   * The form before the skeleton: what the buyer typed must survive their quote reloading. An
   * ended session resets the Apollo store, so after logging back in the quote loads again while
   * the form is open; sending waits for it (see `handleSubmit`).
   */
  if (formOpen) {
    return (
      <UsDeliveryQuoteForm
        defaultValues={quote ? toFormValues(quote) : undefined}
        submitting={submitting || loading}
        requestError={requestError}
        onSubmit={handleSubmit}
        onCancel={closeForm}
      />
    );
  }

  if (!isReady || quoteLoading) {
    return <ProductDeliveryOptionItemListSkeleton />;
  }

  const requestButton = (label: string) => (
    <Button
      appearance="outlined"
      className="self-start"
      onClick={() => void openForm()}
      data-testid="pdp.us-delivery.request"
    >
      {label}
    </Button>
  );

  if (view === "none" || !quote) {
    return (
      <div
        ref={blockRef}
        tabIndex={-1}
        className="flex flex-col gap-3 outline-none"
        data-testid="pdp.us-delivery.quote"
      >
        <Flavor size="medium">
          {t("pdp:pdp-us-delivery-quote-intro", {
            defaultValue: "We check the shipping price for your address first.",
          })}
        </Flavor>
        {requestButton(
          t("pdp:pdp-us-delivery-request-btn", {
            defaultValue: "Request a quote",
          })
        )}
      </div>
    );
  }

  const statusCopy: Record<Exclude<typeof view, "none">, ReactNode> = {
    requested: t("pdp:pdp-us-delivery-status-requested", {
      defaultValue: "Quote requested",
    }),
    quoted: t("pdp:pdp-us-delivery-status-quoted", {
      defaultValue: "Quote ready",
    }),
    expired: t("pdp:pdp-us-delivery-status-expired", {
      defaultValue: "Quote expired",
    }),
    sold: t("pdp:pdp-us-delivery-status-sold", {
      defaultValue: "Quote closed",
    }),
    paid: t("pdp:pdp-us-delivery-status-paid", {
      defaultValue: "Quote paid",
    }),
  };

  return (
    <div
      ref={blockRef}
      tabIndex={-1}
      className="flex flex-col gap-3 outline-none"
      data-testid="pdp.us-delivery.quote"
      data-quote-view={view}
    >
      <div className="flex flex-col gap-1">
        <Flavor size="medium" className="font-bold">
          {t("pdp:pdp-us-delivery-to-zip", {
            postalCode: quote.postalCode,
            defaultValue: "Delivery to {{postalCode}}",
          })}
        </Flavor>
        <Flavor size="medium" className="text-grey-500">
          {t("pdp:pdp-us-delivery-address", {
            city: quote.city,
            region: quote.region,
            deliveryType: deliveryTypeCopy[quote.deliveryType],
            defaultValue: "{{city}}, {{region}} · {{deliveryType}}",
            interpolation: { escapeValue: false },
          })}
        </Flavor>
      </div>
      <Flavor size="medium" className="font-bold">
        {statusCopy[view]}
      </Flavor>
      {view === "requested" && (
        <>
          <Flavor size="medium">
            {t("pdp:pdp-us-delivery-requested-body", {
              defaultValue:
                "We're checking the shipping price for your address. We'll email you as soon as it's ready.",
            })}
          </Flavor>
          <Flavor size="medium">
            {t("pdp:pdp-us-delivery-still-available", {
              defaultValue:
                "Until you buy, the item stays available to other buyers.",
            })}
          </Flavor>
        </>
      )}
      {view === "quoted" && <UsDeliveryQuoteFee quote={quote} />}
      {view === "quoted" && quote.validUntil && (
        <Flavor size="medium">
          {t("pdp:pdp-us-delivery-valid-until", {
            date: formatDeadline(quote.validUntil),
            defaultValue: "Valid until {{date}}.",
          })}
        </Flavor>
      )}
      {view === "expired" && (
        <Flavor size="medium">
          {quote.validUntil
            ? t("pdp:pdp-us-delivery-expired-body", {
                date: formatDeadline(quote.validUntil),
                defaultValue:
                  "This quote was valid until {{date}}. Request a new one for today's price.",
              })
            : t("pdp:pdp-us-delivery-expired-body-no-date", {
                defaultValue:
                  "This quote has expired. Request a new one for today's price.",
              })}
        </Flavor>
      )}
      {view === "sold" && (
        <Flavor size="medium">
          {t("pdp:pdp-us-delivery-sold-body", {
            defaultValue: "The item sold before this quote was paid.",
          })}
        </Flavor>
      )}
      {view === "paid" && (
        <Flavor size="medium">
          {t("pdp:pdp-us-delivery-paid-body", {
            defaultValue: "You bought this item with this delivery quote.",
          })}
        </Flavor>
      )}
      {canRequestAgain(view) &&
        requestButton(
          t("pdp:pdp-us-delivery-request-again-btn", {
            defaultValue: "Request a new quote",
          })
        )}
    </div>
  );
};

/** The quoted fee, shown like every US delivery price (`UsDeliveryPrice`). */
const UsDeliveryQuoteFee = ({
  quote,
}: {
  quote: Pick<DeliveryQuoteFragmentFragment, "fee">;
}) => {
  const { t } = useTranslation();

  if (!quote.fee) {
    return null;
  }

  return (
    <UsDeliveryPrice
      label={t("pdp:pdp-us-delivery-fee-label", {
        defaultValue: "Delivery fee",
      })}
      price={quote.fee}
      data-testid="pdp.us-delivery.fee"
    />
  );
};
